import React from 'react';
import { useTranslation } from 'react-i18next';

import { ICustomTopToolbarProps } from '@private/data-grid';

import Button from 'components/Button';
import Switch from 'components/Controls/Switch';
import ExpandTableSwitch from 'components/Grids/DataGrid/ExpandTableSwitch';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';

import { useDataGridContext } from '../DataGridContext';
import AppliedFilters from '../Filters/AppliedFilters';
import { useFilterContext } from '../Filters/FilterContext';
import Presets from '../Filters/Presets';
import SidebarFilterButton from '../Filters/Sidebar';
import { RightTopBarColumn, SettingsColumn, TableSettings, TopBarRow } from '../styled';

import {
  ColumnsSettings,
  FiltersFallbackWrapper,
  SettingsButton,
  StyledSwitch,
} from './styled';

export interface ICustomTopToolbarFnProps {
  filters: React.ReactNode;
  presets: React.ReactNode;
  rightColumn: React.ReactNode;
}

interface ITopToolbar<T> extends ICustomTopToolbarProps<T> {
  customUpperTopToolbar?: (data: ICustomTopToolbarFnProps) => React.ReactNode;
  hasEditableCells?: boolean;
  isExpandable?: boolean;
  isFilterEnabled: boolean;
  onDownloadReport?: () => void;
  refetch?: () => void;
  withCustomTopRightColumn?: boolean;
}

const TopToolbar = <T,>({
  columnsSettings,
  customUpperTopToolbar,
  hasEditableCells,
  isExpandable,
  isFilterEnabled,
  onDownloadReport,
  refetch,
  withCustomTopRightColumn,
}: ITopToolbar<T>) => {
  const { t } = useTranslation();

  const {
    config: { hideFilters, hidePresets },
    pageKey,
  } = useFilterContext();

  const { gridEditMode, setGridEditMode } = useDataGridContext();

  const { enableColumnStretch, isAllColumnsStretched, toggleAllColumnsStretch } =
    columnsSettings;

  const onToggleAllColumnsStretch = React.useCallback(() => {
    toggleAllColumnsStretch(!isAllColumnsStretched);
  }, [toggleAllColumnsStretch, isAllColumnsStretched]);

  const onChangeEditCellsMode = React.useCallback(() => {
    setGridEditMode(!gridEditMode);
  }, [gridEditMode, setGridEditMode]);

  const fallbackRenderFn = ({ filters, presets }: ICustomTopToolbarFnProps) => (
    <FiltersFallbackWrapper>
      {presets}
      {filters}
    </FiltersFallbackWrapper>
  );

  const renderFn = customUpperTopToolbar || fallbackRenderFn;

  const rightColumn = (
    <RightTopBarColumn>
      <SettingsColumn>
        {isExpandable && pageKey && <ExpandTableSwitch pageKey={pageKey} />}
        {hasEditableCells && (
          <Switch
            checked={gridEditMode}
            label={t('common.gridSettings.gridEditMode')}
            onChange={onChangeEditCellsMode}
            testIds={{ container: 'gridEditCellsModeSwitcher' }}
          />
        )}
        {onDownloadReport && (
          <Button
            iconSize={21}
            onClick={onDownloadReport}
            size='md'
            startIcon={<ICONS_MAP.DownloadFile />}
            testIds={{ container: 'gridDownloadReportButton' }}
            variant='icon'
          />
        )}
        {refetch && (
          <Button
            iconSize={22}
            onClick={refetch}
            size='md'
            startIcon={<ICONS_MAP.ArrowCircle />}
            testIds={{ container: 'gridRefetchButton' }}
            variant='icon'
          />
        )}
      </SettingsColumn>
      {pageKey && (
        <ColumnsSettings
          {...columnsSettings}
          customColumnSwitcher={({ isAllVisible, toggle }) => (
            <StyledSwitch
              checked={isAllVisible}
              label={
                isAllVisible
                  ? t('common.gridSettings.hideAll')
                  : t('common.gridSettings.enableAll')
              }
              onChange={toggle}
            />
          )}
          enableColumnStretch={enableColumnStretch}
          textConfig={t('common.gridSettings', { returnObjects: true })}
        >
          {({ isOpen }) => (
            <SettingsButton
              $isOpen={isOpen}
              iconSize={22}
              size='md'
              startIcon={<ICONS_MAP.Settings />}
              testIds={{ container: 'gridSettingsButton' }}
              variant='icon'
            />
          )}
        </ColumnsSettings>
      )}
      {enableColumnStretch && (
        <Button
          iconSize={22}
          onClick={onToggleAllColumnsStretch}
          size='md'
          startIcon={isAllColumnsStretched ? <ICONS_MAP.Shrink /> : <ICONS_MAP.Stretch />}
          variant='icon'
        />
      )}
    </RightTopBarColumn>
  );

  return (
    <TableSettings>
      <TopBarRow>
        {renderFn({
          filters: isFilterEnabled && !hideFilters && <SidebarFilterButton />,
          presets: isFilterEnabled && !hidePresets && <Presets />,
          rightColumn,
        })}
        {withCustomTopRightColumn ? null : rightColumn}
      </TopBarRow>
      {isFilterEnabled && <AppliedFilters />}
    </TableSettings>
  );
};

export default appReactMemo(TopToolbar);
